import { ToSQLCtx, ToSQLQuery } from '../../sql/to-sql';
import { Column } from '../../../columns/column';
import {
  CteHooks,
  CteTableHook,
  EnsureCountItem,
  HookSelect,
  TableHook,
} from '../../basic-features/select/hook-select';
import { QueryData } from '../../query-data';

export type HookPurpose = 'Create' | 'Update' | 'Delete';

export const addTableHook = (
  ctx: ToSQLCtx,
  q: ToSQLQuery,
  data: QueryData,
  select?: HookSelect,
  hookPurpose?: HookPurpose,
  dontAddTableHook?: boolean,
): void => {
  if (data.ensureCount !== undefined && ctx.cteName) {
    const cteHooks = setCteHooks(ctx, true);
    (cteHooks.ensureCount ??= {})[ctx.cteName] = { count: data.ensureCount };
  }

  const afterCreate = data.afterCreate;
  const afterUpdate = data.afterUpdate;
  const afterSave = data.afterSave;
  const afterDelete = data.afterDelete;
  const afterCreateCommit = data.afterCreateCommit;
  const afterUpdateCommit = data.afterUpdateCommit;
  const afterSaveCommit = data.afterSaveCommit;
  const afterDeleteCommit = data.afterDeleteCommit;
  const throwOnNotFound =
    // Assuming that Create always returns a record, though needs to be cautions with onConflictDoNothing
    hookPurpose !== 'Create' &&
    (data.returnType === 'oneOrThrow' || data.returnType === 'valueOrThrow');

  const hasAfterHook =
    afterCreate ||
    afterUpdate ||
    afterSave ||
    afterDelete ||
    afterCreateCommit ||
    afterUpdateCommit ||
    afterSaveCommit ||
    afterDeleteCommit;

  if (!select && !hasAfterHook && !throwOnNotFound) {
    return;
  }

  const tableHook: TableHook = {
    hookPurpose,
    select,
    afterCreate,
    afterUpdate,
    afterSave,
    afterDelete,
    afterCreateCommit,
    afterUpdateCommit,
    afterSaveCommit,
    afterDeleteCommit,
  };

  if (ctx.cteName && !dontAddTableHook) {
    if (tableHook && (hasAfterHook || throwOnNotFound)) {
      const shape: Column.Shape.Data = {};
      if (tableHook.select) {
        for (const key of tableHook.select.keys()) {
          shape[key] = q.shape[key] as unknown as Column.Pick.Data;
        }
      }

      const item: CteTableHook = {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        table: q.table!,
        shape,
        tableHook: tableHook,
        throwOnNotFound,
      };

      const hasSelect = throwOnNotFound || !!tableHook.select;
      const cteHooks = setCteHooks(ctx, hasSelect);
      (cteHooks.tableHooks ??= {})[ctx.cteName] ??= item;
    }
  } else {
    ctx.topCtx.tableHook = tableHook;
  }
};

export const setCteHooks = (ctx: ToSQLCtx, hasSelect: boolean): CteHooks => {
  if (
    hasSelect &&
    ctx.topCtx.selectList &&
    ctx.topCtx === ctx.topCtx.topCtx &&
    !ctx.topCtx.cteHookTopNullSelectAppended
  ) {
    ctx.topCtx.selectList.push('NULL');
    ctx.topCtx.cteHookTopNullSelectAppended = true;
  }

  if (ctx.topCtx.cteHooks) {
    if (hasSelect) ctx.topCtx.cteHooks.hasSelect = true;
    return ctx.topCtx.cteHooks;
  } else {
    return (ctx.topCtx.cteHooks = {
      hasSelect,
    });
  }
};

export const ensureCTECount = (
  ctx: ToSQLCtx,
  cteName: string,
  countItem: EnsureCountItem,
) => {
  (setCteHooks(ctx, true).ensureCount ??= {})[cteName] = countItem;
};
